import React, { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import BackHomeButton from '../components/BackHomeButton';
import { db, auth } from '../firebase';

function AddResults() {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [docType, setDocType] = useState('');
  const [topicName, setTopicName] = useState('');
  const navigate = useNavigate();

  const user = getAuth().currentUser;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const maxSizeInBytes = 1 * 1024 * 1024; // 1 MB
      if (selectedFile.size > maxSizeInBytes) {
        setMessage('❌ File size exceeds 1 MB. Please select a smaller file.');
        alert('❌ File size exceeds 1 MB. Please select a smaller file.');
        setFile(null);
        return;
      }
      setMessage(''); // clear previous message
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !docType) {
      alert('Please select a file, choose type, and provide a short description if needed');
      return;
    }

    if (!user) {
      alert('User not logged in');
      return;
    }

    const storage = getStorage();
    const userFolder = `user_uploads/${user.email}`;
    const storageRef = ref(storage, `${userFolder}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    setUploading(true);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const prog = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setProgress(prog);
      },
      (error) => {
        console.error(error);
        setUploading(false);
        alert('Error uploading file');
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

        //const db = getFirestore();
        await addDoc(collection(db, 'uploads'), {
          fileName: file.name,
          fileUrl: downloadURL,
          description,
          topicName,
          documentType: docType,
          uploadedBy: user.email,
          uid: user.uid,
          uploadTime: serverTimestamp()
        });

        alert('File uploaded successfully!');
        setUploading(false);
        setFile(null);
        setDescription('');
        setDocType('');
        setTopicName('');
        setProgress(0);
        navigate('/Old_Home');
      }
    );
  };

  return (
    <div style={styles.card}>

      <BackHomeButton style={{ marginTop: '2rem' }} />

      <h2 style={styles.heading}>Upload Your Personal File</h2>

      {message && <p style={styles.message}>{message}</p>}

      <div style={styles.infoBox}>
        <p><strong>User:</strong> {user?.email || 'Not logged in'}</p>
        <p style={styles.thankYouMessage}>
          Keep your past results and certificates in a secured location for future reference.
        </p>
        <p style={styles.alertMessage}>
          Personal uploads are private and never shared with others.
        </p>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Choose File</label>
        <input type="file" onChange={handleFileChange} style={styles.input} />
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Document Type</label>
        <select value={docType} onChange={(e) => setDocType(e.target.value)} style={styles.select}>
          <option value="">Select Type</option>
          <option value="Results">Results</option>
          <option value="Certificates">Certificates</option>
          <option value="Others">Others</option>
        </select>
      </div>

      <div style={styles.inputGroup}>
        <label style={styles.label}>Details (optional)</label>
        <input
          type="text"
          placeholder="Enter document details"
          value={topicName}
          onChange={(e) => setTopicName(e.target.value)}
          style={styles.input}
        />
      </div>

      <button onClick={handleUpload} style={styles.button} disabled={uploading}>
        {uploading ? `Uploading ${progress}%` : 'Upload'}
      </button>

      {uploading && (
        <div style={styles.progressBarWrapper}>
          <div style={{ ...styles.progressBar, width: `${progress}%` }} />
        </div>
      )}

      <footer style={styles.footer}>
        <p style={styles.footerText}>© 2026 Eascode All Rights Reserved.</p>
      </footer>
    </div>
  );
}

const styles = {
  card: {
    maxWidth: '480px',
    margin: '2rem auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  heading: {
    textAlign: 'center',
    fontSize: '1.8rem',
    fontWeight: '700',
    marginBottom: '1.5rem',
    color: '#333',
  },
  infoBox: {
    backgroundColor: '#f1f1f1',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  thankYouMessage: {
    marginTop: '0.5rem',
    color: '#4CAF50',
    fontWeight: '500',
  },
  alertMessage: {
    marginTop: '0.5rem',
    color: '#d32f2f',
    fontWeight: '600',
  },
  inputGroup: {
    marginBottom: '1.2rem',
  },
  label: {
    fontWeight: '600',
    color: '#444',
    marginBottom: '0.3rem',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.9rem',
    border: '1.5px solid #ccc',
    borderRadius: '8px',
    fontSize: '1rem',
  },
  select: {
    width: '100%',
    padding: '0.6rem 0.9rem',
    border: '1.5px solid #ccc',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: '#fff',
  },
  button: {
    width: '100%',
    padding: '0.8rem',
    backgroundColor: '#4285f4',
    color: '#fff',
    fontSize: '1.1rem',
    border: 'none',
    borderRadius: '10px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 10px rgba(66,133,244,0.4)',
    marginTop: '1rem',
  },
  progressBarWrapper: {
    width: '100%',
    backgroundColor: '#f3f3f3',
    borderRadius: '8px',
    height: '10px',
    marginTop: '1rem',
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#4CAF50',
    transition: 'width 0.4s ease',
  },
  message: {
    color: '#4CAF50',
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: '1rem',
  },
  footer: {
    marginTop: '3rem',
    padding: '1rem',
    backgroundColor: '#333',
    textAlign: 'center',
  },
  footerText: {
    color: '#fff',
    fontSize: '0.9rem',
  },
  message: {
  color: '#f44336', // red color for error
  fontWeight: '500',
  textAlign: 'center',
  marginBottom: '1rem',
},
};

export default AddResults;
